import React, { useContext, useMemo, useState } from "react"
import Breadcrumb from "components/Common/Breadcrumb"
import CanView from "components/Common/CanView"
import { t } from "i18next"
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Row,
} from "reactstrap"
import PreviewDocs from "./components/PreviewDocs"
import ExtractedFieldList from "./components/ExtractedFieldList"
import { useQuery } from "@tanstack/react-query"
import useParamsQuery from "./../../hooks/useParamsQuery"
import { getDocuments } from "api/docApi"
import useInitialData from "hooks/useInitialData"
import AnalysisProvider from "context/analysis-context"
import useFetchOptions from "hooks/useFetchOptions"
import Select from "react-select"
import HandleError from "components/Common/HandleError"
import lodash from "lodash"
import SearchInput from "components/Common/SearchInput"

function AnalysisPage() {
  document.title = "analysis | Pixamo"

  const { params, setParams, addParam, deleteParam } = useParamsQuery()
  const [docIndex, setDocIndex] = useState(0)

  const folderSelect = useFetchOptions({
    url: "/documentations/folder/select/",
    queryKey: "folder_select",
  })

  // fetch documents
  const { data, isLoading, error } = useQuery({
    queryKey: ["documents", params],
    queryFn: () => {
      setDocIndex(0)
      return getDocuments(params)
    },
  })
  const { results } = useInitialData(data)

  const handleNextDoc = () => {
    if (docIndex < results.length - 1) setDocIndex(docIndex + 1)
  }

  const handlePrevDoc = () => {
    if (docIndex > 0) setDocIndex(docIndex - 1)
  }

  const documentData = results[docIndex]

  return (
    <CanView allowedUsers={["admin", "employee"]} renderAuthFailed>
      <AnalysisProvider>
        <div className="page-content">
          <Container fluid>
            {/* Render Breadcrumb */}
            <Breadcrumb title={t("home")} breadcrumbItem={t("analysis")} />
            <div className="d-flex justify-content-between align-items-center mb-3">
              <SearchInput
                onSearch={value =>
                  value
                    ? addParam({ upload_file: value })
                    : deleteParam("upload_file")
                }
                defaultValue={params.upload_file}
              />
              <Select
                defaultValue={
                  params.folder && {
                    label: params.folder,
                    value: params.folder,
                  }
                }
                className="w-xl"
                placeholder={t("select folder")}
                onMenuOpen={folderSelect.fetchOptions}
                isLoading={folderSelect.isLoading}
                options={folderSelect.options}
                onChange={option => setParams({ folder: option.label })}
              />
            </div>
            <HandleError
              isLoading={isLoading}
              error={error}
              isEmpty={!results.length}
            >
              <Row>
                <Col md={7}>
                  <Card>
                    <CardHeader className="bg-white d-flex justify-content-between align-items-center">
                      <Button
                        size="sm"
                        onClick={handlePrevDoc}
                        disabled={docIndex === 0}
                      >
                        {t("previous")}
                      </Button>
                      <p className="mb-0">
                        {lodash.truncate(documentData?.filename)}
                      </p>
                      <Button
                        size="sm"
                        onClick={handleNextDoc}
                        disabled={docIndex === results.length - 1}
                      >
                        {t("next")}
                      </Button>
                    </CardHeader>
                    <CardBody>
                      <PreviewDocs document={documentData} />
                    </CardBody>
                  </Card>
                </Col>
                <Col md={5}>
                  <Card className="h-100">
                    <CardBody>
                      <ExtractedFieldList document={documentData} />
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </HandleError>
          </Container>
        </div>
      </AnalysisProvider>
    </CanView>
  )
}

export default AnalysisPage
